import React from 'react';
import { defineMessage, FormattedMessage } from 'react-intl';
import { observer } from 'mobx-react-lite';
import PropTypes from 'prop-types';

import {
    QuestionDateOfBirth,
    QuestionEmailAddress,
    QuestionNames,
    QuestionPhoneNumber,
    WizardPageTemplate,
} from '@ratehub/base-ui';
import { COMMON_MESSAGES, SectionHeader } from '@ratehub/ins-common';
import { GENDERS } from '@ratehub/documents/common/stable';

import QuestionHasConsumedTobacco from '../components/QuestionHasConsumedTobacco';
import QuestionGender from '../components/QuestionGender';


function PersonalDetailsPage({
    hasJointApplicant = false,
    shouldShowGenderQuestion = true,
    values,

    onSubmit,
}) {
    return (
        <WizardPageTemplate
            name={values.name}
            onContinue={onSubmit}
            isAutoFocusEnabled={false}
            messages={{
                TITLE: (
                    <FormattedMessage
                        id="InsLifeWizard.PersonalDetails.title"
                        defaultMessage="Tell us about yourself"
                    />
                ),
            }}
        >
            {/* Primary applicant details */}
            <SectionHeader
                className="rh-mt-3"
                message={(
                    <FormattedMessage
                        id="InsLifeWizard.PersonalDetails.primaryApplicantHeader"
                        defaultMessage="Primary applicant details"
                    />
                )}
            />

            {/* Name */}
            <QuestionNames
                firstName={{
                    value: values.firstName,
                }}
                lastName={{
                    value: values.lastName,
                }}
                tooltipMessage={(
                    <FormattedMessage
                        id="InsLifeWizard.PersonalDetails.nameTooltip"
                        defaultMessage="Please enter your legal name found on government IDs, such as your health card."
                    />
                )}
                isInline
                shouldWrap
            />


            {/* Not required for group insurance */}
            <If condition={shouldShowGenderQuestion}>
                {/* Gender */}
                {/* Ticket INS-2729 created to create a <QuestionGender> in ins-common */}
                {/* Created locally for now in /components */}
                <QuestionGender
                    value={values.gender}
                />

                {/* Date of birth */}
                <QuestionDateOfBirth
                    name="dateOfBirth"
                    value={values.dateOfBirth}
                    tooltipMessage={DATE_OF_BIRTH_TOOLTIP}
                />
            </If>


            {/* Phone number */}
            <QuestionPhoneNumber
                value={values.phone}
                labelMessageVariant="conversational"
                tooltipMessage={(
                    <FormattedMessage
                        id="InsLifeWizard.PersonalDetailsPage.phone.tooltip"
                        defaultMessage="Expect an insurance expert to contact you within 48 business hours to provide you with additional details."
                    />
                )}
            />

            {/* Email address */}
            <QuestionEmailAddress
                value={values.email}
                labelMessageVariant="conversational"
                placeholder={(
                    <FormattedMessage
                        id="InsLifeWizard.PersonalDetailsPage.emailAddress.placeholder"
                        defaultMessage="Enter email address"
                    />
                )}
                tooltipMessage={COMMON_MESSAGES.EMAIL_TOOLTIP}
            />

            {/* IF JOINT APPLICANT */}
            <If condition={hasJointApplicant}>
                {/* Secondary applicant details */}
                <SectionHeader
                    className="rh-mt-3"
                    message={(
                        <FormattedMessage
                            id="InsLifeWizard.PersonalDetails.secondaryApplicantHeader"
                            defaultMessage="Secondary applicant details"
                        />
                    )}
                />

                {/* Joint applicant name */}
                <QuestionNames
                    labelMessage={(
                        <FormattedMessage
                            id="InsLifeWizard.PersonalDetailsPage.jointApplicantName"
                            defaultMessage="What is your spouse or significant other’s name?"
                        />
                    )}
                    firstName={{
                        name: 'jointApplicantFirstName',
                        value: values.jointApplicantFirstName,
                    }}
                    lastName={{
                        name: 'jointApplicantLastName',
                        value: values.jointApplicantLastName,
                    }}
                    tooltipMessage={(
                        <FormattedMessage
                            id="InsLifeWizard.PersonalDetailsPage.jointApplicantNameTooltip"
                            defaultMessage="Please enter your spouse or significant other's legal name found on government IDs, such as a health card."
                        />
                    )}
                    isInline
                    shouldWrap
                />

                {/* Joint applicant gender */}
                {/* Ticket INS-2729 created to create a <QuestionGender> in ins-common */}
                <QuestionGender
                    name="jointApplicantGender"
                    value={values.jointApplicantGender}
                    labelMessage={(
                        <FormattedMessage
                            id="InsLifeWizard.PersonalDetailsPage.jointApplicantGender"
                            defaultMessage="What is your spouse or significant other’s sex?"
                        />
                    )}
                />

                {/* Joint applicant date of birth */}
                <QuestionDateOfBirth
                    name="jointApplicantDateOfBirth"
                    value={values.jointApplicantDateOfBirth}
                    labelMessage={(
                        <FormattedMessage
                            id="InsLifeWizard.PersonalDetailsPage.jointApplicantDateOfBirth"
                            defaultMessage="What is your spouse or significant other’s date of birth?"
                        />
                    )}
                    tooltipMessage={DATE_OF_BIRTH_TOOLTIP}
                />

                {/* Joint applicant has used tobacco */}
                <QuestionHasConsumedTobacco
                    name="hasJointApplicantConsumedTobacco"
                    value={values.hasJointApplicantConsumedTobacco}
                    labelMessage={(
                        <FormattedMessage
                            id="InsLifeWizard.PersonalDetailsPage.hasJointApplicantConsumedTobacco"
                            defaultMessage="Has your spouse or significant other consumed tobacco products within the last 12 months?"
                        />
                    )}
                    tooltipMessage={(
                        <FormattedMessage
                            id="InsLifeWizard.PersonalDetailsPage.hasJointApplicantConsumedTobaccoTooltip"
                            defaultMessage="If your spouse or significant other is a non-smoker, most life insurance companies will offer you favorable rates given your better overall health and longevity."
                        />
                    )}
                />
            </If>

        </WizardPageTemplate>
    );
}

PersonalDetailsPage.propTypes = {
    hasJointApplicant: PropTypes.bool,
    shouldShowGenderQuestion: PropTypes.bool,
    values: PropTypes.shape({
        name: PropTypes.string,
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        gender: PropTypes.oneOf(Object.values(GENDERS)),
        dateOfBirth: PropTypes.string,
        phone: PropTypes.string,
        email: PropTypes.string,
        wantsEmailUpdates: PropTypes.bool,
        jointApplicantFirstName: PropTypes.string,
        jointApplicantLastName: PropTypes.string,
        jointApplicantGender: PropTypes.oneOf(Object.values(GENDERS)),
        jointApplicantDateOfBirth: PropTypes.string,
        hasJointApplicantConsumedTobacco: PropTypes.bool,
    }).isRequired,
    onSubmit: PropTypes.func.isRequired,
};

// Using defineMessage because this tooltip is repeated in DateOfBirth and JointApplicantDateOfBirth
// as opposed to using <FormattedMessage> directly in the props
const DATE_OF_BIRTH_TOOLTIP = defineMessage({
    id: 'InsLifeWizard.PersonalDetails.dateOfBirthTooltip',
    defaultMessage: 'Age is a critical input when receiving a life insurance quote. Younger individuals will generally pay considerably lower insurance premiums, making it important to purchase life insurance even when you’re young to save in the long run.',
});

export default observer(PersonalDetailsPage);
